/**
 * Converts a date-time string into formatted date and time strings.
 *
 * This function takes an ISO 8601 date-time string, converts it to a JavaScript `Date` object,
 * and formats the date and time using `Intl.DateTimeFormat`. The resulting date is in the
 * format `DD MMM YYYY` (e.g., `02 Dec 2024`), and the time is in `h:mm A` format (e.g., `6:00 AM`).
 *
 * @param {string} dateTimeString - The ISO 8601 date-time string to convert (e.g., "2024-12-02T06:00:00Z").
 * @returns {Object} - An object containing formatted `date` and `time` strings:
 *   - `date`: Formatted as 'DD MMM YYYY' (e.g., '02 Dec 2024').
 *   - `time`: Formatted as 'h:mm A' (e.g., '6:00 AM').
 *
 * Example usage:
 * const { date, time } = convertDateTime("2024-12-02T06:00:00Z");
 * console.log(date); // Output: "02 Dec 2024"
 * console.log(time); // Output: "6:00 AM"
 */
export const convertDateTime = (dateTimeString) => {
  if (!dateTimeString) return { date: '', time: '' };

  const formattedDate = new Date(dateTimeString);
  // Format for date: 02 Dec 2024
  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(formattedDate);

  // Format for time: 6:00 AM
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(formattedDate);

  return { date, time };
};

/**
 * A date the server sends ("2026-01-31T00:00:00" or "2026-01-31") as the "YYYY-MM-DD" a date field holds: the
 * date part exactly as written. Never through `new Date(...).toISOString()`, which reads the date as local
 * midnight and then moves it to the day before in any time zone east of UTC (Australia): a season starting
 * 1 January was saved as 31 December, a year earlier, and every competing age moved with it (fixed 2026-10-20).
 *
 * @param {string} serverDate - The date as the server sent it.
 * @returns {string} "YYYY-MM-DD", or '' when there is no date.
 */
export const toDateFieldValue = (serverDate) => {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(serverDate || '');
  return match ? match[1] : '';
};

/**
 * A date field's "YYYY-MM-DD" as local midnight of that day, so it compares correctly with other local dates
 * (today, or a limit made with `new Date(year, month, day)`). `new Date('2026-01-31')` would be UTC midnight
 * instead, which is a different day in every time zone west of UTC and a different hour everywhere else.
 *
 * @param {string} value - "YYYY-MM-DD".
 * @returns {Date|null} Local midnight of that day, or null when the value is not a date.
 */
export const dateFieldToLocalDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

/**
 * Today in the browser's own time zone, as the "YYYY-MM-DD" a date field holds. Never
 * `new Date().toISOString()`, which is today in UTC: yesterday in Australia until 10 or 11 am.
 *
 * @param {Date} [now] - The moment to read; the current time when left out.
 * @returns {string} "YYYY-MM-DD".
 */
export const todayDateFieldValue = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * A date field's "YYYY-MM-DD" as the local date and time the server stores ("2026-10-20T09:30:00"), with no time
 * zone. The time of day is kept from the stored value it replaces, as written, or is midnight. Never through
 * `toISOString()`, which sent 20 October from Australia as 19 October 13:00 UTC, and the server stored that.
 *
 * @param {string} value - "YYYY-MM-DD".
 * @param {string} [originalServerDate] - The stored value being replaced ("2026-10-18T09:30:00"), if any.
 * @returns {string|null} "YYYY-MM-DDTHH:mm:ss", or null when the value is not a date.
 */
export const dateFieldToServerDateTime = (value, originalServerDate) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const time = /T(\d{2}:\d{2}:\d{2})/.exec(originalServerDate || '');
  return `${value}T${time ? time[1] : '00:00:00'}`;
};

/**
 * Converts a time duration in milliseconds into a formatted record string.
 *
 * This function takes a time duration in milliseconds and returns a string
 * representation in the format `MM:SS.mmm` (if minutes are present) or `SS.mmm`
 * (if only seconds and milliseconds are present). The milliseconds are padded
 * with leading zeros based on the specified count.
 *
 * @param {number} milliseconds - The time duration in milliseconds. Must be a non-negative number.
 * @param {number} [millisecondZeroCount=3] - The number of digits to display for milliseconds.
 *   Defaults to 3 (e.g., "003" for milliseconds).
 *
 * @throws {Error} Throws an error if the input milliseconds is negative.
 *
 * @returns {string} The formatted time string in `MM:SS.mmm` or `SS.mmm` format.
 *
 * @example
 * // Example 1: Formatting a time duration with minutes and milliseconds
 * const time1 = convertMillisecondsToRecord(125678);
 * // Output: "2:05.678"
 *
 * // Example 2: Formatting a time duration without minutes
 * const time2 = convertMillisecondsToRecord(45678);
 * // Output: "45.678"
 *
 * // Example 3: Customizing the number of digits for milliseconds
 * const time3 = convertMillisecondsToRecord(125678, 2);
 * // Output: "2:05.67"
 *
 * @notes
 * - Milliseconds are formatted with leading zeros as per the `millisecondZeroCount` parameter.
 * - If the time is less than one minute, the output will exclude the minutes field.
 */
export const convertMillisecondsToRecord = (
  milliseconds,
  millisecondZeroCount = 3
) => {
  if (milliseconds < 0) {
    throw new Error('Milliseconds cannot be negative');
  }

  // Round the total milliseconds to the desired precision FIRST
  // This prevents issues where rounding fractional seconds causes carry-over
  // For example: 181996ms with 2 decimals should round to 182000ms (3:02.00), not 3:01.00
  const precision = 10 ** (3 - millisecondZeroCount); // e.g., 10ms for 2 decimals, 1ms for 3 decimals
  const roundedMilliseconds = Math.round(milliseconds / precision) * precision;

  // Now break down the rounded value into components
  const totalSeconds = Math.floor(roundedMilliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const millis = roundedMilliseconds % 1000;

  // Format milliseconds - since we already rounded, we just need to scale and pad
  const scaledMillis = Math.round(millis / precision);
  const formattedMillis = scaledMillis
    .toString()
    .padStart(millisecondZeroCount, '0')
    .slice(-millisecondZeroCount); // Safety: ensure we never exceed desired length

  // Return the formatted string
  if (minutes > 0) {
    const formattedSeconds = seconds.toString().padStart(2, '0');
    return `${minutes}:${formattedSeconds}.${formattedMillis}`;
  } else {
    const formattedSeconds =
      seconds < 10 ? seconds.toString() : seconds.toString().padStart(2, '0');
    return `${formattedSeconds}.${formattedMillis}`;
  }
};

/**
 * Converts a time record string in various valid formats into milliseconds.
 *
 * Supported formats:
 * - M:SS.s, M:SS.ss, M:SS.sss
 * - MM:SS.s, MM:SS.ss, MM:SS.sss
 * - S.s, S.ss, S.sss (if minutes are 0)
 * - SS.s, SS.ss, SS.sss (if minutes are 0)
 *
 * @param {string} record - The time record string to convert.
 * @returns {number} The time in milliseconds.
 * @throws {Error} If the input format is invalid.
 *
 * Example:
 * convertRecordToMilliseconds("2:24.391");  // 1444391
 * convertRecordToMilliseconds("02:05.5");  // 125500
 * convertRecordToMilliseconds("9.123");    // 9123
 * convertRecordToMilliseconds("12.45");    // 12450
 */
export const convertRecordToMilliseconds = (record) => {
  //console.log(`convertRecordToMilliseconds called with: "${record}"`);
  const recordRegex = /^(?:(\d{1,2}):(\d{2})\.(\d{1,3})|(\d{1,2})\.(\d{1,3}))$/; // Matches valid formats
  const match = record.match(recordRegex);

  if (!match) {
    if (record.includes(':')) {
      throw new Error(
        'Invalid record format. Supported formats for durations more than 1 minute: M:SS.s, M:SS.ss, M:SS.sss, MM:SS.s, MM:SS.ss, MM:SS.sss.'
      );
    } else {
      throw new Error(
        'Invalid record format. Supported formats for durations less than 1 minute: S.s, S.ss, S.sss, SS.s, SS.ss, SS.sss.'
      );
    }
  }

  if (match[1] !== undefined) {
    // Format with minutes
    const minutes = match[1];
    const seconds = match[2];
    const milliseconds = match[3];

    // Normalize milliseconds to 3 digits
    const normalizedMilliseconds = milliseconds.padEnd(3, '0');

    return (
      parseInt(minutes, 10) * 60 * 1000 +
      parseInt(seconds, 10) * 1000 +
      parseInt(normalizedMilliseconds, 10)
    );
  } else {
    // Format without minutes
    const seconds = match[4];
    const milliseconds = match[5];

    // Normalize milliseconds to 3 digits
    const normalizedMilliseconds = milliseconds.padEnd(3, '0');

    return parseInt(seconds, 10) * 1000 + parseInt(normalizedMilliseconds, 10);
  }
};

/**
 * Format a date relative to today: "In 26 days" / "38 days ago" / "Today".
 * Returns '' for empty input; returns the original string if unparseable.
 *
 * Parses "yyyy-mm-dd" by splitting components into a local Date — using
 * `new Date(dateStr)` on a date-only ISO string would treat it as UTC midnight,
 * which surfaces the wrong calendar date in negative-offset timezones.
 */
export const formatExpiresRelative = (dateStr) => {
  if (!dateStr) return '';
  const datePart = String(dateStr).split('T')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return dateStr;
  const [y, m, d] = parts;
  const target = new Date(y, m - 1, d); // local midnight
  if (Number.isNaN(target.getTime())) return dateStr;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays > 0) return `In ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  const past = Math.abs(diffDays);
  return `${past} day${past === 1 ? '' : 's'} ago`;
};
